import { useMemo, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../../components/PageContent";
import { Card } from "../../components/Card";
import { InfoBox } from "../../components/InfoBox";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Modal } from "../../components/Modal";
import { Button, RouterButtonLink } from "../../components/Button";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../components/Table";
import { useGetPaymentMethodsQuery } from "../../query/paymentMethodsQuery";
import { useGetActiveFeaturesQuery } from "../../query/billingItemsQuery";
import { useGetIntegrationsQuery } from "../../query/integrationsQuery";
import { useGetStoreQuery } from "../../query/storesQuery";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { PaymentMethodType } from "@KassaroAB/shared/types/enums/PaymentMethodType";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";

const TypeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const TypeButton = styled(RouterButtonLink)`
  justify-content: flex-start;
`;

const FEATURE_PAYMENT_METHOD_TYPES: [Features, PaymentMethodType][] = [
  [Features.ORDER, PaymentMethodType.ORDER],
  [Features.INVOICE, PaymentMethodType.INVOICE],
  [Features.CREDIT_NOTE, PaymentMethodType.CREDIT_NOTE],
  [Features.GIFT_CARD, PaymentMethodType.GIFT_CARD],
];

const INTEGRATION_PAYMENT_METHOD_TYPES: [IntegrationType, PaymentMethodType[]][] = [
  [IntegrationType.WESTPAY_CLOUD, [PaymentMethodType.WESTPAY_CLOUD]],
  [
    IntegrationType.MARKETPAY,
    [PaymentMethodType.MARKETPAY_CLOUD, PaymentMethodType.MARKETPAY_LOCAL],
  ],
  [IntegrationType.VIVA, [PaymentMethodType.VIVA, PaymentMethodType.VIVA_CLOUD]],
  [IntegrationType.SOFTPAY, [PaymentMethodType.SOFTPAY]],
  [IntegrationType.NETS, [PaymentMethodType.NETS_TERMINAL]],
];

export const PaymentMethodsView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const [isTypePickerOpen, setIsTypePickerOpen] = useState(false);
  const { data: paymentMethods, isLoading, error } =
    useGetPaymentMethodsQuery(storeId);
  const { data: store } = useGetStoreQuery(storeId);
  const { data: activeFeatures } = useGetActiveFeaturesQuery(storeId ?? null);
  const { data: integrations } = useGetIntegrationsQuery(store?.chainId ?? null);

  const selectableTypes = useMemo(
    () => [
      PaymentMethodType.CASH,
      PaymentMethodType.SWISH_COMPANY,
      PaymentMethodType.SWISH_COMMERCE,
      PaymentMethodType.OTHER,
      ...FEATURE_PAYMENT_METHOD_TYPES.filter(([feature]) =>
        activeFeatures?.includes(feature),
      ).map(([, type]) => type),
      ...INTEGRATION_PAYMENT_METHOD_TYPES.filter(([integrationType]) =>
        integrations?.some((integration) => integration.type === integrationType),
      ).flatMap(([, types]) => types),
    ],
    [activeFeatures, integrations],
  );

  if (isLoading) {
    return (
      <PageContent title={t("paymentMethodsView.title")}>
        <LoadingContainer message={t("paymentMethodsView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("paymentMethodsView.title")}
      actions={
        <Button onClick={() => setIsTypePickerOpen(true)}>
          {t("paymentMethodsView.create")}
        </Button>
      }
    >
      {error && (
        <InfoBox variant="error">{t("paymentMethodsView.errorLoad")}</InfoBox>
      )}

      {paymentMethods && paymentMethods.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("paymentMethodsView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("paymentMethodsView.tableType")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {paymentMethods.map((item) => (
                <TableRow
                  key={item.id}
                  to={`/stores/${storeId}/paymentMethods/${item.id}`}
                >
                  <TableCell noWrap>{item.name}</TableCell>
                  <TableCell noWrap>
                    {t(`paymentMethodType.${item.type}`, {
                      defaultValue: item.type,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        !error && (
          <Card>
            <div style={{ textAlign: "center", color: colors.textMuted }}>
              {t("paymentMethodsView.empty")}
            </div>
          </Card>
        )
      )}

      <Modal
        isOpen={isTypePickerOpen}
        onClose={() => setIsTypePickerOpen(false)}
        title={t("paymentMethodsView.selectType")}
        maxWidth={420}
      >
        <TypeList>
          {selectableTypes.map((type) => (
            <TypeButton
              key={type}
              to={`/stores/${storeId}/paymentMethods/new?type=${type}`}
              $variant="outlined"
              $color="secondary"
            >
              {t(`paymentMethodType.${type}`, { defaultValue: type })}
            </TypeButton>
          ))}
        </TypeList>
      </Modal>
    </PageContent>
  );
};
